/**
 * Sidebar "has answers" dot logic for OASIS section navigation.
 * Counts meaningful answers in `formData` (prefill, sync, prior saves, manual
 * edits). Skips hidden subitems and default "No" on Yes/No fields.
 */
import { resolveYesNoOptionPair } from './bespoke/primitives/yesNoRadio';
import type { OASISFormData, Question, FormValue } from './types';
import { getSelectedSubitems } from './utils';

const hasNavigationValue = (value: FormValue | undefined): boolean => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

/** Whether a field value should count toward the section "has answers" sidebar dot. */
export const countsAsNavigationAnswer = (
  question: Question,
  value: FormValue | undefined
): boolean => {
  if (!hasNavigationValue(value)) return false;

  const pair = resolveYesNoOptionPair(question.values);
  if (pair && String(value).trim() === pair.noValue) return false;

  return true;
};

/**
 * Count answered questions in a section for sidebar navigation.
 * Walks visible classic fields (top-level + revealed subitems).
 */
export const countNavigationAnsweredQuestions = (
  questions: Question[],
  formData: OASISFormData
): number => {
  let count = 0;

  const visit = (question: Question) => {
    if (countsAsNavigationAnswer(question, formData[question.qid])) {
      count++;
    }
    for (const subitem of getSelectedSubitems(
      question,
      formData[question.qid]
    )) {
      visit(subitem);
    }
  };

  for (const question of questions) visit(question);
  return count;
};
